import { ApolloError, useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useIsPaymentOptional } from './CreateAppointmentForm.hooks';
import { StripeContext } from 'components/CreditCardForm/types/creditCardForm';
import { CreateAppointmentResponseData, Membership, ValidPromo, AppointmentType } from 'kb-shared';
import {
  bookingAnalytics,
  CREDIT_CARD_STATES,
  CreditCardState
} from 'kb-shared/booking/utils/bookingAnalytics';
import { PATIENT_APPOINTMENTS } from 'kb-shared/graphql/queries';
import { isCoachingAppointment } from 'kb-shared/utilities/appointment.helper';
import { BugTracker } from 'kb-shared/utilities/bugTracker';
import { analytics } from 'utilities/analytics';
import { showErrorToast } from 'utilities/notificationUtils';

import { CREATE_APPOINTMENT } from '../Confirm.graphql';
import { errorIsOverlappingAppointmentError, generateErrorString } from '../Confirm.utils';
import { AppointmentData, Props } from './CreateAppointmentForm.types';
import { PaymentForm } from './PaymentForm';

export const CreateAppointmentForm = ({
  product,
  timeSlot,
  onBookAppointment,
  handleValidPromo,
  providerId,
  triggerSubmitAction,
  setIsConfirmationDisabled
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [validPromo, setValidPromo] = useState<ValidPromo | null>(null);
  const { isPaymentOptional } = useIsPaymentOptional(product?.data as AppointmentType | Membership);
  const [loadingStripeToken, setLoadingStripeToken] = useState(false);
  const [inputValid, setInputValid] = useState(false);
  const [stripeContext, setStripeContext] = useState<StripeContext | undefined>(undefined);
  const [kbStripeCardStripeIdentifierSelected, setKbStripeCardStripeIdentifier] = useState('');
  const [iscreditCardFieldEmpty, setIsCreditCardFieldEmpty] = useState(true);

  const navigate = useNavigate();

  // only used in payment form
  const [createAppointment] = useMutation<
    CreateAppointmentResponseData,
    {
      timeSlotId: number;
      appointmentTypeId: number;
      stripeTokenId?: string | null;
      discountCode?: string | null;
      kbStripeCardStripeIdentifier?: string;
      providerId?: number;
    }
  >(CREATE_APPOINTMENT, {
    refetchQueries: [{ query: PATIENT_APPOINTMENTS }]
  });

  if (product?.type !== 'appointment' || !timeSlot) return null;

  const purchaseAppointmentSucceeded = ({
    appointmentData,
    creditCardUsed,
    creditCardState
  }: {
    appointmentData?: AppointmentData;
    creditCardUsed: boolean;
    creditCardState: CreditCardState;
  }) => {
    if (!appointmentData) return;

    setLoading(false);
    onBookAppointment(appointmentData.appointment);
    bookingAnalytics.track.npvAppointmentBookingSucceeded(
      appointmentData.appointment,
      creditCardUsed,
      creditCardState
    );
    bookingAnalytics.track.appointmentBookingSucceeded(
      appointmentData.appointment,
      creditCardUsed,
      creditCardState
    );
  };

  const onError = (err: ApolloError | null) => {
    const errorString = generateErrorString(err);

    analytics.track(analytics.EVENTS.APPOINTMENT_BOOKING_FAILED, { error: errorString });

    if (err && errorIsOverlappingAppointmentError(err)) {
      showErrorToast('Selected appointment was taken in the mean time. Please select a new one.');
      navigate(-1);
      return;
    }

    setLoading(false);
    showErrorToast(errorString);
  };

  const appointmentType = product.data;
  const shouldChargeAtBooking = product.data.shouldChargeAtBooking;
  const canSkipPayment =
    appointmentType.rateCents === 0 ||
    validPromo?.totalCents === 0 ||
    (shouldChargeAtBooking === false && isCoachingAppointment(appointmentType));

  const createAppointmentWithoutPaymentInfo = async () => {
    try {
      const response = await createAppointment({
        variables: {
          timeSlotId: parseInt(timeSlot.id, 10),
          appointmentTypeId: parseInt(appointmentType.id, 10),
          providerId: providerId
        }
      });
      purchaseAppointmentSucceeded({
        appointmentData: response?.data?.createAppointment,
        creditCardUsed: false,
        creditCardState: CREDIT_CARD_STATES.NONE
      });
    } catch (error) {
      onError(error as ApolloError);
    }
  };
  const noPayment = canSkipPayment ? createAppointmentWithoutPaymentInfo : undefined;
  const onPaymentOptional = isPaymentOptional ? createAppointmentWithoutPaymentInfo : undefined;

  const onSubmit = async (stripeTokenId?: string, kbStripeCardStripeIdentifier?: string) => {
    setLoading(true);
    if (stripeTokenId) {
      createAppointment({
        variables: {
          providerId: providerId,
          timeSlotId: parseInt(timeSlot.id, 10),
          appointmentTypeId: parseInt(appointmentType.id, 10),
          stripeTokenId,
          discountCode: validPromo?.code
        }
      })
        .then(response => {
          purchaseAppointmentSucceeded({
            appointmentData: response.data?.createAppointment,
            creditCardUsed: true,
            creditCardState: CREDIT_CARD_STATES.NEW
          });
        })
        .catch(onError);
    } else if (kbStripeCardStripeIdentifier) {
      createAppointment({
        variables: {
          providerId: providerId,
          timeSlotId: parseInt(timeSlot.id, 10),
          appointmentTypeId: parseInt(appointmentType.id, 10),
          kbStripeCardStripeIdentifier,
          discountCode: validPromo?.code
        }
      })
        .then(response => {
          purchaseAppointmentSucceeded({
            appointmentData: response.data?.createAppointment,
            creditCardUsed: true,
            creditCardState: CREDIT_CARD_STATES.EXISTING
          });
        })
        .catch(onError);
    } else {
      onError(null);
    }
  };

  const onPaymentFormSubmit = async () => {
    if (noPayment) return noPayment();

    // if payment is optional and there is no card info, submit without card info
    // otherwise, go through the normal flow(validate card, get token, submit)
    if (onPaymentOptional && !kbStripeCardStripeIdentifierSelected && iscreditCardFieldEmpty) {
      return onPaymentOptional();
    }

    if (stripeContext) {
      setLoadingStripeToken(true);
      const tokenResponse = await stripeContext.stripe.createToken(stripeContext.cardNumberElement);
      setLoadingStripeToken(false);
      if (tokenResponse.error) {
        const tokenResponseError = tokenResponse.error.message;
        if (tokenResponseError) {
          showErrorToast(tokenResponseError);
          BugTracker.notify(tokenResponseError, 'Stripe create token');
        }
        return;
      }
      const stripeTokenId = tokenResponse.token?.id;
      onSubmit(stripeTokenId);
      return;
    }

    if (kbStripeCardStripeIdentifierSelected) {
      onSubmit(undefined, kbStripeCardStripeIdentifierSelected);
    }
  };

  useEffect(() => {
    if (triggerSubmitAction) {
      onPaymentFormSubmit();
    }
  }, [triggerSubmitAction]);

  // TODO: add confirmation disabled logic to Confirm.tsx
  const isConfirmationDisabled =
    noPayment || onPaymentOptional
      ? loading
      : loading ||
        !inputValid ||
        (!stripeContext && !kbStripeCardStripeIdentifierSelected) ||
        loadingStripeToken;

  useEffect(() => {
    setIsConfirmationDisabled(isConfirmationDisabled);
  }, [isConfirmationDisabled]);

  return (
    <PaymentForm
      product={product}
      loading={loading}
      onStripeTokenLoading={isLoading => setLoadingStripeToken(isLoading)}
      onValidPromoChange={validPromo => {
        handleValidPromo(validPromo);
        setValidPromo(validPromo);
      }}
      setStripeContext={data => setStripeContext(data)}
      setKbStripeCardStripeIdentifier={stripeIdentifier =>
        setKbStripeCardStripeIdentifier(stripeIdentifier)
      }
      setIsCreditCardFieldEmpty={isEmpty => setIsCreditCardFieldEmpty(isEmpty)}
      setIsInputValid={isValid => setInputValid(isValid)}
      onSubmit={onPaymentFormSubmit}
    />
  );
};
